import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useContext } from 'react';
import { useDrop } from 'react-dnd';
import { cloneDeep } from 'lodash';
import update from 'immutability-helper';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useDPPState } from '../Context/index';
import ElementTemplateDPP from './TemplateComponent/ElementTemplateDPP';
import { deleteDPPObejctsFromS3 } from '../../../../services/s3Services';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import GoogleMapContext from '../../../../Components/GoogleMapComponent/GoogleMapContext';

function DraggableItem({
  item,
  index,
  onMove,
  onChange,
  deleteItem,
  editingInput,
  dppAddEditAccess,
  onClick,
  handleChildDrop,
  editingCustomInput,
  deleteCustomSectionItem,
  handleDrop,
  libraryBasicData,
}) {
  const controlRef = useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: 'item',
    drop: (item, monitor) => {
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      handleDrop(item, index);
    },
    collect: (monitor) => ({
      item: monitor.getItem(),
      handlerId: monitor.getHandlerId(),
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  return (
    <div ref={drop(controlRef)} className="dpp-item">
      {isOver && <div className="dpp-drag-placeholder">Drop here</div>}
      <ElementTemplateDPP
        fromForm
        dppEle={item}
        dataIndex={index}
        moveCard={(dragIndex, hoverIndex) => onMove(dragIndex, hoverIndex)}
        key={index}
        item={item}
        index={index}
        onChange={onChange}
        deleteItem={deleteItem}
        editingInput={editingInput}
        id={item?.id}
        keys={item?.key}
        dppAddEditAccess={dppAddEditAccess}
        onClick={onClick}
        handleChildDrop={handleChildDrop}
        editingCustomInput={editingCustomInput}
        deleteCustomSectionItem={deleteCustomSectionItem}
        libraryBasicData={libraryBasicData}
      />
    </div>
  );
}

function DPPCanvasPage({ canvasData, DPPAddEditAccess, onClick, updateParentCanvasArray, libraryBasicData }, ref) {
  const { t } = useTranslation('common');
  const customRef = useRef(null);
  const { currentCoordinates, placesService } = useContext(GoogleMapContext);

  const { setDppState } = useDPPState();

  const [showConfirmationPopup, setShowConfirmationPopup] = useState({
    modal: false,
    index: -1,
    sectionIndex: -1,
  });
  const { sectionIndex, index } = showConfirmationPopup;

  const [state, setState] = useState({
    dppLocalState: [],
  });
  const { dppLocalState } = state;

  function hasGoogleNearPlaces(data) {
    for (const item of data) {
      // Check if current item matches
      if (item.key === 'google' && item.isAddressType === 'nearPlaces') {
        return true;
      }

      // Check nested controls if available
      if (Array.isArray(item.controls)) {
        if (hasGoogleNearPlaces(item.controls)) {
          return true;
        }
      }
    }
    return false;
  }

  useEffect(() => {
    const callFn = async () => {
      let clonedData = cloneDeep(canvasData);
      const checkGoogleNearbyPlacesExist = hasGoogleNearPlaces(canvasData);
      if (checkGoogleNearbyPlacesExist) {
        clonedData = await setGooglNearByPlacesAsPerUserCoordinates(clonedData);
      }
      setState((prevState) => ({
        ...prevState,
        dppLocalState: clonedData,
      }));
    };

    callFn();
  }, [canvasData]);

  function appendNearMe(input) {
    const searchTerm = input?.trim()?.toLowerCase(); // Convert input to lowercase for comparison
    if (searchTerm?.includes('near me')) {
      return input; // Return the original input if it already contains "near me"
    }

    return `${input} near me`; // Otherwise, concatenate "near me"
  }

  const getNearbyPlaces = async (placesService, location, searchTerm) => {
    const request = {
      location: new window.google.maps.LatLng(location.lat, location.lng),
      radius: 5000,
      keyword: appendNearMe(searchTerm),
    };

    try {
      const results = await new Promise((resolve, reject) => {
        placesService.nearbySearch(request, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK || status === 'ZERO_RESULTS') {
            resolve(results);
          } else {
            reject(new Error(`Places API error: ${status}`));
          }
        });
      });

      const first10Results = results?.slice(0, 10) || [];
      const placeDetailsPromises = first10Results.map((place) => {
        return new Promise((resolve, reject) => {
          placesService.getDetails({ placeId: place.place_id }, (placeDetails, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(placeDetails);
            } else {
              reject(new Error(`Place details error: ${status}`));
            }
          });
        });
      });

      return await Promise.all(placeDetailsPromises);
    } catch (error) {
      console.error('Error in getNearbyPlaces:', error);
      toasterMsg(`Error fetching nearby places: ${error.message}`, 'danger');
      return [];
    }
  };

  const setGooglNearByPlacesAsPerUserCoordinates = async (canvasData) => {
    const currLocation = currentCoordinates?.lat && currentCoordinates.lng ? currentCoordinates : { lat: 20.36343, lng: 72.923448 };

    const updatedCanvasData = [...canvasData];

    for (let i = 0; i < updatedCanvasData.length; i++) {
      const item = updatedCanvasData[i];
      if (item?.placesNearMe && item?.key === 'google' && item?.isAddressType === 'nearPlaces') {
        try {
          const placeDetailsArray = await getNearbyPlaces(placesService, currLocation, item.placesNearMe);
          item.relatedPlacesNearMe = placeDetailsArray;
        } catch (error) {
          item.relatedPlacesNearMe = [];
        }
      }
      if (item?.key === 'custom_section') {
        item.controls.forEach(async (control) => {
          if (control?.placesNearMe && control?.key === 'google' && control?.isAddressType === 'nearPlaces') {
            try {
              const placeDetailsArray = await getNearbyPlaces(placesService, currLocation, control.placesNearMe);
              control.relatedPlacesNearMe = placeDetailsArray;
            } catch (error) {
              control.relatedPlacesNearMe = [];
            }
          }
        });
      }
    }

    return updatedCanvasData;
  };

  useImperativeHandle(ref, () => ({
    getActivePageData() {
      return dppLocalState;
    },
  }));

  const handleDrop = (draggedItem, targetIndex) => {
    const cloneArr = [...dppLocalState];

    if (draggedItem.index !== undefined) {
      const [removed] = cloneArr.splice(draggedItem.index, 1);
      if (removed) cloneArr.splice(targetIndex, 0, removed);
    } else if (draggedItem) {
      cloneArr.splice(targetIndex, 0, draggedItem);
    }

    setState((prevState) => ({
      ...prevState,
      dppLocalState: cloneArr,
    }));
    updateParentCanvasArray(cloneArr);
  };

  const deleteItem = async (gridIndex) => {
    setShowConfirmationPopup({ modal: !showConfirmationPopup?.modal, index: gridIndex });
  };

  const deleteByIds = async () => {
    const clonedDPPData = JSON.parse(JSON.stringify(dppLocalState));
    const deleteKeys = [];
    const deleteImageDPPData = clonedDPPData?.find((_, index) => index === showConfirmationPopup.index);
    if (deleteImageDPPData?.key === 'product_journey') {
      if (deleteImageDPPData?.transportIcon && deleteImageDPPData?.transportIcon?.includes('dpp_assets')) {
        deleteKeys.push(deleteImageDPPData?.transportIcon);
      }
      if (deleteImageDPPData?.tier?.length > 0) {
        deleteImageDPPData?.tier?.map((tierData) => {
          if (tierData?.tierIcon && tierData?.tierIcon?.includes('dpp_assets')) {
            deleteKeys.push(tierData.tierIcon);
          }
          if (tierData?.ethicalAuditIcon && tierData?.ethicalAuditIcon?.includes('dpp_assets')) {
            deleteKeys.push(tierData.ethicalAuditIcon);
          }
        });
      }
    }
    if (deleteImageDPPData?.key === 'testing_control') {
      if (deleteImageDPPData?.testGroup?.length > 0) {
        deleteImageDPPData?.testGroup?.map((testData) => {
          if (testData?.testIconImage && testData?.testIconImage?.includes('dpp_assets')) {
            deleteKeys.push(testData.testIconImage);
          }
        });
      }
    }
    if (deleteImageDPPData?.key === 'heading') {
      if (deleteImageDPPData?.iconPath && deleteImageDPPData?.iconPath?.includes('dpp_assets')) {
        deleteKeys.push(deleteImageDPPData?.iconPath);
      }
    }
    if (deleteImageDPPData?.key === 'company_stamp') {
      if (deleteImageDPPData?.iconCompanyPath && deleteImageDPPData?.iconCompanyPath?.includes('dpp_assets')) {
        deleteKeys.push(deleteImageDPPData?.iconCompanyPath);
      }
    }
    if (deleteImageDPPData?.key === 'url_link') {
      if (deleteImageDPPData?.metaData?.image && deleteImageDPPData?.metaData?.image?.includes('dpp_assets')) {
        deleteKeys.push(deleteImageDPPData?.metaData?.image);
      }
    }
    if (deleteImageDPPData?.key === 'navigation') {
      if (deleteImageDPPData?.anchorMenu?.length > 0) {
        deleteImageDPPData?.anchorMenu?.map((testData) => {
          if (testData?.defaultIcon && testData?.defaultIcon?.includes('dpp_assets')) {
            deleteKeys.push(testData.defaultIcon);
          }
        });
      }
    }
    if (deleteImageDPPData?.key === 'custom_section') {
      for (const con of deleteImageDPPData?.controls || []) {
        if (con?.key === 'testing_control') {
          if (con?.testGroup?.length > 0) {
            con?.testGroup?.map((testData) => {
              if (testData?.testIconImage && testData?.testIconImage?.includes('dpp_assets')) {
                deleteKeys.push(testData.testIconImage);
              }
            });
          }
        }
        if (con?.key === 'heading') {
          if (con?.iconPath && con?.iconPath?.includes('dpp_assets')) {
            deleteKeys.push(con?.iconPath);
          }
        }
        if (con?.key === 'company_stamp') {
          if (con?.iconCompanyPath && con?.iconCompanyPath?.includes('dpp_assets')) {
            deleteKeys.push(con?.iconCompanyPath);
          }
        }
        if (con?.key === 'url_link') {
          if (con?.metaData?.image && con?.metaData?.image?.includes('dpp_assets')) {
            deleteKeys.push(con?.metaData?.image);
          }
        }
        if (con?.key === 'care_user_instruction') {
          if (con?.careLabelDescriptionArr?.length > 0) {
            con?.careLabelDescriptionArr?.map((testData) => {
              if (testData?.image && testData?.image?.includes('dpp_assets')) {
                deleteKeys.push(testData.image);
              }
            });
          }
        }
      }
    }
    if (deleteImageDPPData?.key === 'care_user_instruction') {
      if (deleteImageDPPData?.careLabelDescriptionArr?.length > 0) {
        deleteImageDPPData?.careLabelDescriptionArr?.map((testData) => {
          if (testData?.image && testData?.image?.includes('dpp_assets')) {
            deleteKeys.push(testData.image);
          }
        });
      }
    }
    if (deleteKeys?.length) {
      await deleteDPPObejctsFromS3({ objectKeys: deleteKeys });
    }
    const filteredData = clonedDPPData.filter((_, index) => index !== showConfirmationPopup.index);
    setState((prevState) => ({
      ...prevState,
      dppLocalState: filteredData,
    }));
    updateParentCanvasArray(filteredData);

    setShowConfirmationPopup(false);
  };

  const editingInput = (itemIndex) => {
    const clonedDPPData = cloneDeep(dppLocalState);
    const editedDataObj = clonedDPPData[itemIndex];
    setDppState((oldState) => ({
      ...oldState,
      editPopup: true,
      editpopupData: editedDataObj,
      editPopupItemIndex: itemIndex,
      editCustomSectionIndex: -1,
    }));
  };

  const onChange = (value, itemIndex, keyName) => {
    const clonedDPPData = cloneDeep(dppLocalState);
    const changesItem = clonedDPPData[itemIndex];

    if (keyName === 'heading') {
      changesItem.value = value;
    }
    setState((prevState) => ({
      ...prevState,
      dppLocalState: clonedDPPData,
    }));
  };

  const moveCard = (dragIndex, hoverIndex) => {
    if (Number.isInteger(dragIndex) && Number.isInteger(hoverIndex)) {
      const updatedSeqList = update(dppLocalState, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dppLocalState[dragIndex]],
        ],
      });

      // Add sequenceId to each object in the updatedSeqList
      const updatedSeqListWithSequenceId = updatedSeqList.map((item, index) => ({
        ...item,
        sequenceId: index,
      }));

      setState((prevState) => ({
        ...prevState,
        dppLocalState: [...updatedSeqListWithSequenceId],
      }));
      updateParentCanvasArray(updatedSeqListWithSequenceId);
    }
  };

  const handleChildDrop = (item, monitor, targetId, parentIndex, childIndex) => {
    // Clone the state to avoid mutating it

    const cloneArr = [...dppLocalState];

    // Clone the controls array for the specified parentIndex
    const controls = [...cloneArr[parentIndex].controls];

    // Add the item at the specified childIndex or at the end if childIndex is null
    if (childIndex !== null && childIndex >= 0) {
      controls.splice(childIndex, 0, item); // Insert at childIndex
    } else {
      controls.push(item); // Add to the end of the array
    }

    // Update the controls in the parent array
    cloneArr[parentIndex].controls = controls;

    // Update the state with the modified array
    setState((prevState) => ({
      ...prevState,
      dppLocalState: cloneArr,
    }));

    // Notify the parent canvas about the update
    updateParentCanvasArray(cloneArr);
  };

  const editingCustomInput = (itemIndex, parentIndex) => {
    const clonedDPPData = cloneDeep(dppLocalState);
    const editedDataObj = clonedDPPData[parentIndex];
    const editControlObj = editedDataObj.controls[itemIndex];
    setDppState((oldState) => ({
      ...oldState,
      editPopup: true,
      editpopupData: editControlObj,
      editPopupItemIndex: parentIndex,
      editCustomSectionIndex: itemIndex,
    }));
  };

  const deleteCustomSectionItem = async (itemIndex, parentIndex) => {
    setShowConfirmationPopup({ modal: !showConfirmationPopup?.modal, index: parentIndex, sectionIndex: itemIndex });
  };

  const deleteCustomSectoinConstrols = async () => {
    const clonedDPPData = JSON.parse(JSON.stringify(dppLocalState));
    const deleteKeys = [];
    if (index >= 0 && sectionIndex >= 0) {
      const deleteImageDPPData = clonedDPPData?.[index]?.controls?.[sectionIndex];
      if (deleteImageDPPData?.key === 'testing_control') {
        if (deleteImageDPPData?.testGroup?.length > 0) {
          deleteImageDPPData?.testGroup?.map((testData) => {
            if (testData?.testIconImage && testData?.testIconImage?.includes('dpp_assets')) {
              deleteKeys.push(testData.testIconImage);
            }
          });
        }
      }
      if (deleteImageDPPData?.key === 'heading') {
        if (deleteImageDPPData?.iconPath && deleteImageDPPData?.iconPath?.includes('dpp_assets')) {
          deleteKeys.push(deleteImageDPPData?.iconPath);
        }
      }
      if (deleteImageDPPData?.key === 'company_stamp') {
        if (deleteImageDPPData?.iconCompanyPath && deleteImageDPPData?.iconCompanyPath?.includes('dpp_assets')) {
          deleteKeys.push(deleteImageDPPData?.iconCompanyPath);
        }
      }
      if (deleteImageDPPData?.key === 'url_link') {
        if (deleteImageDPPData?.metaData?.image && deleteImageDPPData?.metaData?.image?.includes('dpp_assets')) {
          deleteKeys.push(deleteImageDPPData?.metaData?.image);
        }
      }
      if (deleteKeys?.length) {
        await deleteDPPObejctsFromS3({ objectKeys: deleteKeys });
      }
      clonedDPPData[index].controls = clonedDPPData?.[index]?.controls.filter((_, index) => index !== sectionIndex);
      setState((prevState) => ({
        ...prevState,
        dppLocalState: clonedDPPData,
      }));

      setShowConfirmationPopup(false);
    }
  };

  const handleparentDrop = (item) => {
    if (item.key === 'custom_section') {
      item.uniqueId = uuidv4();
    }
    const cloneArr = [...dppLocalState];
    cloneArr.push(item);
    setState((prevState) => ({
      ...prevState,
      dppLocalState: cloneArr,
    }));
    updateParentCanvasArray(cloneArr);
  };

  const [{ isOverParent }, drop] = useDrop({
    accept: 'item',
    drop: (item, monitor) => {
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      handleparentDrop(item);
    },
    collect: (monitor) => ({
      item: monitor.getItem(),
      isOverParent: monitor.isOver({ shallow: true }),
    }),
  });
  return (
    <>
      <div ref={drop(customRef)} className="dpp-container">
        {dppLocalState?.map((item, index) => (
          <React.Fragment key={`${item?.id}_${index}`}>
            <DraggableItem
              fromForm
              item={item}
              index={index}
              onMove={(dragIndex, hoverIndex) => handleDrop({ index: dragIndex, col: item }, hoverIndex)}
              dppEle={item}
              onChange={onChange}
              deleteItem={deleteItem}
              editingInput={editingInput}
              dataIndex={index}
              id={item?.id}
              keys={item?.key}
              moveCard={moveCard}
              dppAddEditAccess={DPPAddEditAccess}
              onClick={onClick}
              handleChildDrop={handleChildDrop}
              editingCustomInput={editingCustomInput}
              deleteCustomSectionItem={deleteCustomSectionItem}
              handleDrop={handleDrop}
              libraryBasicData={libraryBasicData}
            />
          </React.Fragment>
        ))}

        {/* Placeholder at the end of the list */}
        {isOverParent && <div className="dpp-drag-placeholder">Drop here</div>}
      </div>

      {showConfirmationPopup?.modal && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup?.modal}
          onConfirm={() => (sectionIndex >= 0 ? deleteCustomSectoinConstrols() : deleteByIds())}
          loading={false}
          title={t('DPP.DELETE_CONTROL')}
          message={t('DPP.DELETE_IMAGE_MESSAGE')}
          secondMessage={t('DPP.DELETE_IMAGE_MESSAGE_2')}
          toggle={() => setShowConfirmationPopup(!showConfirmationPopup?.modal)}
        />
      )}
    </>
  );
}
const DPPContainer = forwardRef(DPPCanvasPage);
export default DPPContainer;
